import { format } from "date-fns";
import { de } from "date-fns/locale";
import { computed, type Ref } from "vue";

import type { ContentDetailItem } from "@/types/content.d.ts";
import { MediaType } from "@/types/enums.ts";
import formatFileSize from "@/utils/formatFileSize.ts";

export const useAssetData = (
  contentItem: Ref<ContentDetailItem | null>,
  defaultHeight: number = 450,
  defaultWidth: number = 670,
) => {
  const pictureAsset = computed(() => {
    return contentItem.value?.assets?.find((asset) => asset.type === "picture");
  });

  const thumbnailRendition = computed(() => {
    return pictureAsset.value?.renditions.find(
      (rendition) => rendition.type === "thumbnail",
    );
  });

  const masonryRendition = computed(() => {
    return pictureAsset.value?.renditions?.find(
      (rendition) => rendition.type === "masonry",
    );
  });

  const originalPicture = computed(() => {
    return pictureAsset.value?.renditions.find(
      (rendition) => rendition.type === "ORIG",
    );
  });

  const mediaFormat = computed(() => {
    const width = masonryRendition.value?.width ?? defaultWidth;
    const height = masonryRendition.value?.height ?? defaultHeight;
    return {
      width,
      height,
    };
  });

  const metadata = computed(() => {
    return {
      headline: contentItem.value?.headline ?? "",
      creditline: contentItem.value?.creditLine ?? "",
      photographer: contentItem.value?.byline ?? undefined,
      source: contentItem.value?.contentSource ?? "",
      rights: contentItem.value?.creditLine ?? "",
      location: contentItem.value?.pois?.[0]?.locality?.name ?? "",
      dateOfRecord: contentItem.value
        ? format(
            new Date(contentItem.value.contentCreated ?? ""),
            "EEEEEE, dd.MM.yyy",
            {
              locale: de,
            },
          )
        : "",
      ...(contentItem.value?.mediaType !== MediaType.GRAPHIC
        ? {
            orientation:
              contentItem.value?.orientation === "horizontal"
                ? "LANDSCAPE "
                : "PORTRAIT",
          }
        : {}),
      width: originalPicture.value?.width ?? "",
      height: originalPicture.value?.height ?? "",
      fileSize: formatFileSize(originalPicture.value?.sizeBytes ?? 0),
      priority: contentItem.value?.urgency || 1,
    };
  });

  return {
    pictureAsset,
    thumbnailRendition,
    masonryRendition,
    originalPicture,
    metadata,
    mediaFormat,
  };
};
